import React, { useMemo } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * MobileBottomSheet
 *
 * Drop-in replacement for Dialog that renders as a bottom sheet on mobile.
 * On desktop: standard centered Dialog (no visual changes).
 * On mobile: full-width bottom sheet with slide-up animation.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void — same as Dialog's onOpenChange
 * - children: modal body content
 * - title: optional header title
 * - description: optional header description
 * - icon: optional Lucide icon component
 * - iconBg: optional icon background color
 * - iconColor: optional icon color
 * - className: className for DialogContent (desktop) AND content wrapper (mobile)
 * - hideCloseButton: hide X button on desktop Dialog
 * - contentClassName: extra classes for mobile content wrapper (legacy)
 */
const MobileBottomSheet = ({
  open,
  onOpenChange,
  children,
  title,
  accessibleTitle,
  description,
  icon: IconComp,
  iconBg,
  iconColor,
  className,
  hideCloseButton,
  contentClassName,
}) => {
  const isMobile = useIsMobile();

  // Parse className to detect p-0 (content manages own padding)
  const hasNoPadding = useMemo(() => {
    if (!className) return false;
    return /\bp-0\b/.test(className);
  }, [className]);

  // Radix may receive Escape before its layer index updates after mounting.
  const handleContentKeyDown = (event) => {
    if (event.key !== "Escape" || event.defaultPrevented || !onOpenChange) return;
    if (event.target.closest?.('[role="dialog"], [role="alertdialog"]') !== event.currentTarget)
      return;
    event.preventDefault();
    event.stopPropagation();
    onOpenChange(false);
  };

  // --- Desktop: standard Dialog ---
  if (!isMobile) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange || (() => {})}>
        <DialogContent
          className={className}
          hideCloseButton={hideCloseButton}
          onKeyDown={handleContentKeyDown}
          // Sem descrição visual, declara a ausência (Radix avisa no console se faltar).
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          {accessibleTitle && <DialogTitle className="sr-only">{accessibleTitle}</DialogTitle>}
          {(title || IconComp) && (
            <DialogHeader>
              <div className="flex items-center gap-3">
                {IconComp && (
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: iconBg || "#f3f4f6" }}
                  >
                    <IconComp className="w-5 h-5" style={{ color: iconColor || "#6b7280" }} />
                  </div>
                )}
                <div>
                  {title && (
                    <DialogTitle className="text-[17px] font-bold text-gray-900 font-outfit">
                      {title}
                    </DialogTitle>
                  )}
                  {description && (
                    <DialogDescription className="text-[13px] text-gray-500 font-outfit">
                      {description}
                    </DialogDescription>
                  )}
                </div>
              </div>
            </DialogHeader>
          )}
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  // --- Mobile: bottom sheet ---
  return (
    <Dialog open={open} onOpenChange={onOpenChange || (() => {})}>
      <DialogPrimitive.Portal>
        {/* Mesmos tokens do ui/dialog: um Dialog aberto por cima (confirmações) fica acima por
            ordem no DOM, e menus/selects (z 9999) não ficam sob o sheet. */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          style={{ zIndex: "var(--z-modal-backdrop)" }}
        />

        {/* Bottom Sheet */}
        <DialogPrimitive.Content
          onKeyDown={handleContentKeyDown}
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl animate-mbs-slideUp flex flex-col outline-none"
          style={{
            zIndex: "var(--z-modal)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            maxHeight: "92vh",
          }}
        >
          {accessibleTitle && <DialogTitle className="sr-only">{accessibleTitle}</DialogTitle>}
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
            <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
          </div>

          {/* Content — respects p-0 from className */}
          <div
            className={`${hasNoPadding ? "" : "px-6 pb-6"} overflow-y-auto flex-1 ${contentClassName || ""}`}
            style={{ maxHeight: "calc(92vh - 40px)" }}
          >
            {/* Optional header with icon */}
            {(title || IconComp) && (
              <div className={`flex items-center gap-3 mb-4 ${hasNoPadding ? "px-6 pt-2" : ""}`}>
                {IconComp && (
                  <div
                    className="h-12 w-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: iconBg || "#f3f4f6" }}
                  >
                    <IconComp className="h-6 w-6" style={{ color: iconColor || "#6b7280" }} />
                  </div>
                )}
                <div className="flex-1">
                  {title && (
                    <DialogTitle className="text-lg font-semibold text-slate-900 font-outfit">
                      {title}
                    </DialogTitle>
                  )}
                  {description && (
                    <p className="text-sm text-slate-500 font-outfit">{description}</p>
                  )}
                </div>
              </div>
            )}

            {children}
          </div>
          {/* Slide Up Animation */}
          <style>{`
          @keyframes mbs-slideUp {
            0% { transform: translateY(100%); }
            100% { transform: translateY(0); }
          }
          .animate-mbs-slideUp {
            animation: mbs-slideUp 0.3s ease-out forwards;
          }
        `}</style>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};

export default MobileBottomSheet;
